import { Column, Container, Link, Row, Text } from "@react-email/components";
import type { PickupPointBlockProps } from "./types";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { useVariablesValues } from "../../lib/variables-context";
import { tLayout } from "../../translations";

export function PickupPointBlockEmail({
  theme,
  section,
  isEditor,
  locale,
  defaultLocale,
}: PickupPointBlockProps & { isEditor?: boolean }) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const { getVariableValue } = useVariablesValues();
  const t = tLayout(locale, defaultLocale);

  const isPickupPoint = getVariableValue("address.isPickupPoint") === true;

  if (!isPickupPoint && !isEditor) {
    return null;
  }

  // Not perfect, but prevents the block from being displayed when the pickup point is not set
  if (!getVariableValue("pickupPoint.addressLine1")?.length) {
    return null;
  }

  const openingHours = getVariableValue("pickupPoint.openingHours");

  const formatDayHours = (
    dayHours: { from: string; to: string }[] | undefined,
  ) => {
    if (!dayHours || dayHours.length === 0) return t("pickupPoint.closed");
    return dayHours.map((h) => `${h.from} - ${h.to}`).join(", ");
  };

  const daysOfWeek = [
    { key: "monday", label: t("pickupPoint.days.monday") },
    { key: "tuesday", label: t("pickupPoint.days.tuesday") },
    { key: "wednesday", label: t("pickupPoint.days.wednesday") },
    { key: "thursday", label: t("pickupPoint.days.thursday") },
    { key: "friday", label: t("pickupPoint.days.friday") },
    { key: "saturday", label: t("pickupPoint.days.saturday") },
    { key: "sunday", label: t("pickupPoint.days.sunday") },
  ];

  return (
    <Container>
      <Row className="mb-3" style={{ color: colors.text }}>
        <Column style={{ width: "100%", verticalAlign: "top" }}>
          <Text
            style={{
              fontWeight: "bold",
              marginBottom: "8px",
              marginTop: "0",
              fontSize: "14px",
            }}
          >
            {t("pickupPoint.title")}
          </Text>

          <Text
            style={{
              fontWeight: "bold",
              margin: "0",
              marginBottom: "4px",
              fontSize: "12px",
            }}
          >
            {getVariableValue("pickupPoint.name")}
          </Text>

          <Text style={{ margin: "0", lineHeight: "1.3", fontSize: "11px" }}>
            {getVariableValue("pickupPoint.addressLine1")}
          </Text>
          {getVariableValue("pickupPoint.addressLine2") && (
            <Text style={{ margin: "0", lineHeight: "1.3", fontSize: "11px" }}>
              {getVariableValue("pickupPoint.addressLine2")}
            </Text>
          )}
          <Text
            style={{
              margin: "0",
              lineHeight: "1.3",
              fontSize: "11px",
              marginBottom: "8px",
            }}
          >
            {getVariableValue("pickupPoint.postalCode")}{" "}
            {getVariableValue("pickupPoint.city")},{" "}
            {getVariableValue("pickupPoint.countryCode")}
          </Text>

          {!!openingHours &&
            !!(
              openingHours.monday?.length ||
              openingHours.tuesday?.length ||
              openingHours.wednesday?.length ||
              openingHours.thursday?.length ||
              openingHours.friday?.length ||
              openingHours.saturday?.length ||
              openingHours.sunday?.length
            ) && (
              <>
                <Text
                  style={{
                    fontWeight: "bold",
                    marginTop: "8px",
                    marginBottom: "4px",
                    fontSize: "12px",
                  }}
                >
                  {t("pickupPoint.openingHours")}
                </Text>

                {daysOfWeek.map(({ key, label }) => {
                  const hours = openingHours[key as keyof typeof openingHours];
                  return (
                    <Row key={key} style={{ marginBottom: "2px" }}>
                      <Column style={{ width: "40%" }}>
                        <Text
                          style={{
                            margin: "0",
                            fontSize: "10px",
                            lineHeight: "1.3",
                          }}
                        >
                          {label}
                        </Text>
                      </Column>
                      <Column style={{ width: "60%" }}>
                        <Text
                          style={{
                            margin: "0",
                            fontSize: "10px",
                            lineHeight: "1.3",
                          }}
                        >
                          {formatDayHours(hours)}
                        </Text>
                      </Column>
                    </Row>
                  );
                })}
              </>
            )}

          {getVariableValue("pickupPoint.addressLine1") &&
            getVariableValue("pickupPoint.postalCode") &&
            getVariableValue("pickupPoint.city") &&
            getVariableValue("pickupPoint.countryCode") && (
              <Text style={{ marginTop: "8px", fontSize: "11px" }}>
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${getVariableValue("pickupPoint.addressLine1")}${
                      getVariableValue("pickupPoint.addressLine2")
                        ? ` ${getVariableValue("pickupPoint.addressLine2")}`
                        : ""
                    } ${getVariableValue("pickupPoint.postalCode")} ${getVariableValue(
                      "pickupPoint.city",
                    )} ${getVariableValue("pickupPoint.countryCode")}`,
                  )}`}
                  style={{
                    color: theme.primaryColor || "#000000",
                    textDecoration: "underline",
                  }}
                >
                  {t("pickupPoint.directions")}
                </Link>
              </Text>
            )}
        </Column>
      </Row>
    </Container>
  );
}
